// src/pages/CapturesPage.jsx — saved HAR captures, one click from the top bar.
// Formerly Admin → HAR History. Saved HARs are what IU Finder searches and what
// Ask can be pointed at, so finding and reopening them shouldn't sit under Admin.
import React, { useEffect, useState } from 'react';
import { listSavedHars, setFavorite, deleteHar, downloadHar } from '../api/client';
import { confirmDelete } from '../utils/confirmDelete';

export default function CapturesPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [q, setQ] = useState('');
    const [dlErr, setDlErr] = useState('');

    const fetchList = async () => {
        setLoading(true); setErr('');
        try {
            const data = await listSavedHars({ q, limit: 200, sort: 'createdAt' });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('HAR list error', e);
            setErr(`Failed to load saved captures: ${e.message}`);
        } finally { setLoading(false); }
    };
    useEffect(() => { fetchList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fav = async (id, want) => {
        try { await setFavorite(id, want); setRows(rs => rs.map(x => (x.id === id ? { ...x, favorite: want } : x))); }
        catch (e) { alert(`Failed to update favorite: ${e.message}`); }
    };
    const del = async (id) => {
        if (!confirmDelete('this saved HAR session', { detail: 'Permanently removes it from the server. This cannot be undone.' })) return;
        try { await deleteHar(id); setRows(rs => rs.filter(x => x.id !== id)); }
        catch (e) { alert(`Delete failed: ${e.message}`); }
    };
    // api.download throws on a non-OK response; surface it instead of an unhandled rejection.
    const download = async (id) => {
        setDlErr('');
        try { await downloadHar(id); }
        catch (e) { setDlErr(`Download failed: ${(e && e.message) || 'unknown error'}`); }
    };
    const open = (id) => { window.open(`/?har=${encodeURIComponent(id)}`, '_self'); };
    // Every IU in this capture — IU Finder with the HAR pre-picked, no search term needed.
    const findIus = (id) => { window.open(`/checks?mode=iu&har=${encodeURIComponent(id)}`, '_self'); };

    const iconBtn = { padding: '4px 8px', background: 'var(--card)', color: 'var(--ink)' };

    return (
        <div style={{ padding: 20, fontFamily: 'var(--font-sans)', maxWidth: 1200, margin: '0 auto' }}>
            <h2 style={{ margin: '0 0 4px' }}>Captures</h2>
            <p style={{ margin: '0 0 14px', color: 'var(--muted)', fontSize: 13 }}>
                Saved HAR captures. Open one to review it on the Dashboard, or find every ad unit in it. Save a HAR after each review so IU Finder and Ask can use it later.
            </p>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') fetchList(); }}
                    className="search search compact" style={{ maxWidth: 260 }} />
                <button onClick={fetchList} className="btn">Refresh</button>
            </div>

            {loading && <div style={{ color: 'var(--muted)' }}>Loading…</div>}
            {!!err && (
                <div role="alert" style={{ background: 'var(--error-bg)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                    {err}
                    <div style={{ color: 'var(--muted)', marginTop: 4, fontSize: 12 }}>
                        Check you're logged in and the backend is reachable.
                    </div>
                </div>
            )}

            {!!dlErr && (
                <div role="alert" style={{ background: 'var(--error-bg)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                    {dlErr}
                </div>
            )}

            {!loading && !err && rows.length === 0 && (
                <div style={{ color: 'var(--muted)' }}>No saved captures yet — save a HAR from the Dashboard's Requests toolbar.</div>
            )}

            {!loading && !err && rows.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid var(--line)' }}>
                            <th style={{ textAlign: 'left', padding: '8px', width: '45%' }}>Name</th>
                            <th style={{ textAlign: 'left', padding: '8px', width: '22%' }}>Date</th>
                            <th style={{ textAlign: 'right', padding: '8px', width: '33%' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.id} style={{ borderBottom: '1px solid var(--line)' }}>
                                <td style={{ padding: '8px', maxWidth: 0 }} title={r.title || '(untitled)'}>
                                    <div className="ellipsis" style={{ fontWeight: 500 }}>
                                        {r.favorite ? '★ ' : ''}{r.title || '(untitled)'}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>ID: {r.id.slice(0, 8)}</div>
                                </td>
                                <td style={{ padding: '8px', color: 'var(--muted)' }}>
                                    {new Date(r.createdAt || r.created_ts || Date.now()).toLocaleString()}
                                </td>
                                <td style={{ padding: '8px' }}>
                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                        <button onClick={() => open(r.id)} className="btn primary" style={{ padding: '4px 8px' }}>Open</button>
                                        <button onClick={() => findIus(r.id)} className="btn" style={iconBtn} title="Every ad unit (IU) in this capture">Find IUs</button>
                                        <button onClick={() => download(r.id)} className="btn" style={iconBtn} title="Download HAR">⬇</button>
                                        <button onClick={() => fav(r.id, !r.favorite)} className="btn" style={{ ...iconBtn, color: r.favorite ? 'orange' : 'var(--muted)' }} title={r.favorite ? 'Unfavorite' : 'Favorite (kept past retention)'}>
                                            {r.favorite ? '★' : '☆'}
                                        </button>
                                        <button onClick={() => del(r.id)} className="btn danger" style={{ padding: '4px 8px' }} title="Delete">✕</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
